import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Plus, Edit, Trash2, Save, X, Code2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminCompanyCategories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {name, slug?, order, active}
  const [busy, setBusy] = useState(false);
  const [svgRaw, setSvgRaw] = useState("");

  const refresh = () => api.get("/company-categories").then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); }, []);

  const newCat = () => { setSvgRaw(""); setEditing({ name: "", slug: "", icon_url: "", order: items.length, active: true, featured: false, _new: true }); };

  // Converte SVG colado em data URI (limpa fill/stroke do Google Fonts e força currentColor)
  const applySvg = () => {
    let s = (svgRaw || "").trim();
    if (!s.toLowerCase().startsWith("<svg")) {
      toast.error("Cole o código SVG completo (começa com <svg ...>)");
      return;
    }
    // Garante xmlns
    if (!/xmlns=/.test(s)) s = s.replace(/<svg/i, '<svg xmlns="http://www.w3.org/2000/svg"');
    // Remove quebras e comentários XML
    s = s.replace(/<!--[\s\S]*?-->/g, "").replace(/\s+/g, " ").trim();
    // base64 (suporta unicode)
    const b64 = btoa(unescape(encodeURIComponent(s)));
    const dataUri = `data:image/svg+xml;base64,${b64}`;
    setEditing((p) => ({ ...p, icon_url: dataUri }));
    toast.success("SVG aplicado ao ícone");
  };

  const clearIcon = () => { setEditing((p) => ({ ...p, icon_url: "" })); setSvgRaw(""); };

  const save = async () => {
    if (!editing?.name?.trim()) { toast.error("Informe o nome"); return; }
    setBusy(true);
    try {
      const payload = {
        name: editing.name.trim(),
        slug: editing.slug || undefined,
        icon_url: editing.icon_url?.trim() || null,
        order: parseInt(editing.order) || 0,
        active: !!editing.active,
        featured: !!editing.featured,
      };
      if (editing._new) {
        await api.post("/company-categories", payload);
        toast.success("Categoria criada");
      } else {
        await api.patch(`/company-categories/${editing._origSlug}`, payload);
        toast.success("Categoria atualizada");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Erro ao salvar");
    } finally { setBusy(false); }
  };

  const del = async (cat) => {
    if (!window.confirm(`Excluir categoria "${cat.name}"?`)) return;
    try {
      await api.delete(`/company-categories/${cat.slug}`);
      toast.success("Categoria excluída");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Erro ao excluir");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-company-categories">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-blue-950">Categorias de Empresa</h1>
          <p className="text-sm text-stone-500 mt-1">Lista oficial que aparece nos formulários de cadastro/edição de empresa.</p>
        </div>
        <Button onClick={newCat} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="cat-new">
          <Plus size={16} className="mr-1.5"/>Nova categoria
        </Button>
      </div>

      <Card className="rounded-2xl border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 w-16">Ícone</th>
                <th className="text-left px-4 py-3 w-16">Ordem</th>
                <th className="text-left px-4 py-3">Nome</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Slug</th>
                <th className="text-left px-4 py-3">Destaque</th>
                <th className="text-left px-4 py-3">Ativa</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.slug} className="border-t border-stone-100" data-testid={`cat-row-${c.slug}`}>
                  <td className="px-4 py-3">
                    {c.icon_url ? (
                      <img src={c.icon_url} alt={c.name} className="w-9 h-9 rounded-full object-cover bg-stone-100 border border-stone-200"/>
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-stone-100 border border-dashed border-stone-300 flex items-center justify-center text-stone-400 text-[10px]">—</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-stone-500">{c.order}</td>
                  <td className="px-4 py-3 font-medium text-stone-900">{c.name}</td>
                  <td className="px-4 py-3 text-stone-500 font-mono text-xs hidden md:table-cell">{c.slug}</td>
                  <td className="px-4 py-3">
                    {c.featured ? <span className="text-amber-700 text-xs font-bold">★ HOME</span> : <span className="text-stone-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {c.active ? <span className="text-green-700 text-xs font-bold">ATIVA</span> : <span className="text-stone-400 text-xs font-bold">INATIVA</span>}
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => { setSvgRaw(""); setEditing({ ...c, _origSlug: c.slug }); }} data-testid={`cat-edit-${c.slug}`}><Edit size={14}/></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(c)} className="text-red-600 hover:text-red-700" data-testid={`cat-del-${c.slug}`}><Trash2 size={14}/></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-stone-400 text-sm">Nenhuma categoria cadastrada</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editing._new ? "Nova categoria" : "Editar categoria"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} className="rounded-xl mt-1" data-testid="cat-form-name"/></div>
              <div><Label>Slug (opcional — gerado automaticamente)</Label><Input value={editing.slug || ""} onChange={(e) => setEditing({...editing, slug: e.target.value})} className="rounded-xl mt-1 font-mono text-xs" placeholder="ex: restaurante" data-testid="cat-form-slug"/></div>
              <div>
                <Label>Ícone (URL da imagem PNG/SVG)</Label>
                <Input value={editing.icon_url || ""} onChange={(e) => setEditing({...editing, icon_url: e.target.value})} className="rounded-xl mt-1" placeholder="https://... ou data:image/svg+xml;base64,..." data-testid="cat-form-icon-url"/>
                {editing.icon_url && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={editing.icon_url} alt="preview" className="w-12 h-12 rounded-full object-cover bg-stone-100 border border-stone-200" data-testid="cat-icon-preview"/>
                    <span className="text-[11px] text-stone-500 flex-1">Preview do ícone circular</span>
                    <button type="button" onClick={clearIcon} className="text-[11px] text-red-500 hover:text-red-700 inline-flex items-center gap-1" data-testid="cat-icon-clear"><X size={11}/>Limpar</button>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-dashed border-blue-300 bg-blue-50/40 p-3 space-y-2">
                <Label className="flex items-center gap-1.5 text-blue-900">
                  <Code2 size={14}/> Ou cole o SVG (Google Fonts Icons)
                </Label>
                <Textarea
                  value={svgRaw}
                  onChange={(e) => setSvgRaw(e.target.value)}
                  rows={3}
                  className="rounded-xl font-mono text-[11px] resize-y"
                  placeholder='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960"...> ... </svg>'
                  data-testid="cat-form-svg-raw"
                />
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={applySvg} className="rounded-lg" data-testid="cat-form-svg-apply">
                    <Save size={13} className="mr-1"/>Aplicar SVG
                  </Button>
                  <p className="text-[10px] text-stone-500 leading-tight">Em <a href="https://fonts.google.com/icons" target="_blank" rel="noreferrer" className="underline text-blue-700">fonts.google.com/icons</a> selecione o ícone → aba &quot;SVG&quot; → &quot;Copy SVG&quot; → cole acima.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ordem</Label><Input type="number" value={editing.order} onChange={(e) => setEditing({...editing, order: e.target.value})} className="rounded-xl mt-1" data-testid="cat-form-order"/></div>
                <div className="flex items-center gap-2 mt-6"><Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({...editing, active: v})} data-testid="cat-form-active"/><Label>Ativa</Label></div>
              </div>
              <div className="flex items-center gap-2 p-2.5 rounded-xl bg-amber-50 border border-amber-200">
                <Switch checked={!!editing.featured} onCheckedChange={(v) => setEditing({...editing, featured: v})} data-testid="cat-form-featured"/>
                <Label className="flex-1">
                  <span className="font-bold text-amber-900">Destacar na Home Mobile</span>
                  <p className="text-[10px] text-amber-800">Aparece no carrossel de ícones acima do banner (até 9 destaques)</p>
                </Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl"><X size={14} className="mr-1"/>Cancelar</Button>
              <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="cat-form-save"><Save size={14} className="mr-1"/>{busy ? "Salvando..." : "Salvar"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
